import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import 'containers/Intelligence/theme/fonts';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../OrbitContext';
import { useOrbitThemeVariant } from '../OrbitThemeScope';
import { TOrbit } from '../i18n/TModules';
import { orbitColorsFor, orbitTokens } from '../styles';
import useBottomBarOffset from '../useBottomBarOffset';
import { useOrbitAccess } from '../useOrbitAccess';
import OrbitMark from './OrbitMark';
import OrbitMarkWhite from './OrbitMarkWhite';

// Diameter of the `perx_3_redesign` avatar and the glyph slot inside it (plan.md, decision 2).
const AVATAR_SIZE = 40;
const AVATAR_MARK_SIZE = 22;

export const ORBIT_SIDEBAR_ID = 'orbit-sidebar';

// The ongoing-session dot on the tile's top-right corner; the 2px white ring stays inset, and 10 is
// the whole dot, ring included.
const StatusDot = styled.span<{ $avatarOnly: boolean }>`
  position: absolute;
  z-index: 2;
  top: -1.98px;
  right: -3.25px;
  box-sizing: border-box;
  width: 10px;
  height: 10px;
  border: 2px solid ${orbitTokens.launcherCard};
  border-radius: 50%;
  background: ${orbitTokens.launcherBadge};

  /* Nothing reveals it on the avatar, so it rides the circle's own top-right edge and stays up. */
  ${({ $avatarOnly }) =>
    $avatarOnly
      ? css`
          top: 0;
          right: 0;
          opacity: 1;
          visibility: visible;
        `
      : null}
`;

const LauncherButton = styled.button<{ $avatarOnly: boolean }>`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56.92px;
  height: 56.92px;
  padding: 8.94px;
  border: none;
  /* 5017:10252 and 5017:6363 both read radius 8, which settles v2's 6-vs-8 coin toss (AF-12). */
  border-radius: 8px;
  background: ${orbitTokens.launcherTile};
  color: ${orbitTokens.launcherCard};
  cursor: pointer;
  font-family: ${orbitTokens.fontFamily};
  /* 5017:10253: a 39.03 x 39.85 glyph slot at inset 8.94, solid white. */
  font-size: 39.03px;
  line-height: 39.85px;

  /* The tile is the whole control, so this ring is the only
     keyboard affordance the launcher has. */
  &:focus-visible {
    outline: 2px solid ${orbitTokens.action};
    outline-offset: 3px;
  }

  .anticon {
    font-size: 39.03px;
  }

  ${({ $avatarOnly }) =>
    $avatarOnly
      ? css`
          width: ${AVATAR_SIZE}px;
          height: ${AVATAR_SIZE}px;
          padding: 0;
          border-radius: 50%;
          /* The circle floats over the page background, and under the light variant its tile and
             that background are two neighbouring near-whites, so the shape had no edge at all. A
             box-shadow rather than a border: it adds no box, so the 36px circle stays 36px. The dark
             reading of the token is the CSS keyword transparent, which draws nothing. */
          box-shadow: 0 0 0 1px ${orbitTokens.launcherRing};
          font-size: ${AVATAR_MARK_SIZE}px;
          line-height: ${AVATAR_MARK_SIZE}px;

          .anticon {
            font-size: ${AVATAR_MARK_SIZE}px;
          }
        `
      : null}
`;

/**
 * Resting offset from the bottom-right corner, and the gap the launcher keeps above a step form's
 * fixed bottom action bar when one is mounted. The bar lays its buttons out flush right, so without
 * the lift the tile sits on top of the primary button (user screenshot, PLA-8844).
 */
export const LAUNCHER_BOTTOM = 26;

const LauncherRoot = styled.div<{ $avatarOnly: boolean; $bottom: number }>`
  position: fixed;
  right: 26px;
  bottom: ${({ $bottom }) => $bottom}px;
  z-index: 900;
  width: ${({ $avatarOnly }) => ($avatarOnly ? `${AVATAR_SIZE}px` : '56.92px')};
  height: ${({ $avatarOnly }) => ($avatarOnly ? `${AVATAR_SIZE}px` : '56.92px')};
  /* Matches the panel's own open curve, so a route that swaps a bar in glides rather than jumps. */
  transition: bottom 0.24s cubic-bezier(0.32, 0.72, 0, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Floating entry point to Orbit. Mounts only once access resolves to `allowed`: a floating button has
 * no Forbidden page to fall back on, so `loading` and `denied` both render nothing. Hidden while the
 * sidebar is open because the fixed corner would otherwise sit over the panel's composer.
 *
 * The tile alone (`5017:10250`); the white label card that held "Orbit" and the context line was
 * removed (user, 2026-10-14), so an ongoing session shows only as the status dot.
 */
const OrbitLauncher: React.FC = () => {
  const access = useOrbitAccess();
  const barOffset = useBottomBarOffset();
  const { open, toggleSidebar, sessionId } = useOrbitContext();
  const { t } = useMultiLanguageTranslation();
  const themeVariant = useOrbitThemeVariant();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  /**
   * Reclaims focus after the panel closes. The launcher unmounts itself while the panel is open, so
   * the node the panel remembers as its opener is detached by the time it tries to hand focus back
   * and focus has fallen to `<body>`; this remount is the only place that can catch it. Guarded on
   * focus actually being lost, so a close that left focus somewhere deliberate is not overridden.
   */
  useEffect(() => {
    if (wasOpen.current && !open && document.activeElement === document.body) {
      buttonRef.current?.focus({ preventScroll: true });
    }

    wasOpen.current = open;
  }, [open]);

  if (access !== 'allowed' || open) {
    return null;
  }

  // UAT feedback (2026-09-15, plan.md item 5): under the light variant the launcher is the avatar.
  const avatarOnly = themeVariant === 'light';

  return (
    <LauncherRoot
      $avatarOnly={avatarOnly}
      $bottom={Math.max(barOffset, LAUNCHER_BOTTOM)}
      data-testid="orbit-launcher-root"
    >
      <LauncherButton
        ref={buttonRef}
        type="button"
        $avatarOnly={avatarOnly}
        aria-label={t(`${TOrbit}.title`)}
        aria-expanded={false}
        aria-controls={ORBIT_SIDEBAR_ID}
        data-testid="orbit-launcher"
        onClick={toggleSidebar}
      >
        {avatarOnly ? (
          <OrbitMark
            width={AVATAR_MARK_SIZE}
            height={AVATAR_MARK_SIZE}
            fill={orbitColorsFor(themeVariant).mark}
          />
        ) : (
          /* 5017:10253 / 5017:6364, the designer's export: five white vectors at 39.03 x 39.85. */
          <OrbitMarkWhite width={39.03} height={39.85} />
        )}
      </LauncherButton>
      {sessionId !== null ? (
        <StatusDot
          $avatarOnly={avatarOnly}
          aria-hidden="true"
          data-testid="orbit-launcher-status"
        />
      ) : null}
    </LauncherRoot>
  );
};

export default OrbitLauncher;
